import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Users, Plus, Edit2, Trash2, Mail, UserCheck, UserX,
  Search, Briefcase, Shield, Building2, LayoutDashboard,
  FileText, LogOut, Menu, X, Settings, Copy, Eye, Check, KeyRound
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../components/ui/alert-dialog";
import { Switch } from "../components/ui/switch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvestigadoresPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, empresa, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [investigadores, setInvestigadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvestigador, setEditingInvestigador] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [investigadorToDelete, setInvestigadorToDelete] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    cargo: "",
    perfil: "investigador",
    ativo: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [createdUserName, setCreatedUserName] = useState("");

  const handleLogout = () => {
    logout();
    navigate(`/${slug}/login`);
  };

  useEffect(() => {
    if (user) {
      fetchInvestigadores();
    }
  }, [slug, user]);

  const fetchInvestigadores = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/empresas/${slug}/investigadores`);
      setInvestigadores(response.data);
    } catch (error) {
      toast.error(t("investigators.messages.errorLoad"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim() || !formData.email.trim()) {
      toast.error(t("common.required"));
      return;
    }

    setSubmitting(true);
    try {
      if (editingInvestigador) {
        await axios.put(
          `${API_URL}/api/empresas/${slug}/investigadores/${editingInvestigador.id}`,
          formData
        );
        toast.success(t("investigators.messages.updated"));
      } else {
        const response = await axios.post(`${API_URL}/api/empresas/${slug}/investigadores`, formData);
        const senhaGerada = response.data.senha_gerada;
        if (senhaGerada) {
          setCreatedUserName(formData.nome);
          setGeneratedPassword(senhaGerada);
          setPasswordCopied(false);
          setPasswordDialogOpen(true);
        }
        toast.success(t("investigators.messages.created"));
      }
      setDialogOpen(false);
      resetForm();
      fetchInvestigadores();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("investigators.messages.errorCreate"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (investigador) => {
    setEditingInvestigador(investigador);
    setFormData({
      nome: investigador.nome,
      email: investigador.email,
      cargo: investigador.cargo || "",
      perfil: investigador.perfil || "investigador",
      ativo: investigador.ativo
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!investigadorToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/empresas/${slug}/investigadores/${investigadorToDelete.id}`);
      toast.success(t("investigators.messages.deleted"));
      fetchInvestigadores();
    } catch (error) {
      toast.error(t("investigators.messages.errorDelete"));
    } finally {
      setDeleteConfirmOpen(false);
      setInvestigadorToDelete(null);
    }
  };

  const toggleAtivo = async (investigador) => {
    try {
      await axios.put(
        `${API_URL}/api/empresas/${slug}/investigadores/${investigador.id}`,
        { ativo: !investigador.ativo }
      );
      toast.success(t("investigators.messages.updated"));
      fetchInvestigadores();
    } catch (error) {
      toast.error(t("investigators.messages.errorUpdate"));
    }
  };

  const handleResetPassword = async (investigador) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/empresas/${slug}/investigadores/${investigador.id}/reset-password`
      );
      const { senha_gerada, nome } = response.data;
      setCreatedUserName(nome);
      setGeneratedPassword(senha_gerada);
      setPasswordCopied(false);
      setPasswordDialogOpen(true);
      toast.success("Senha redefinida com sucesso");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao redefinir senha");
    }
  };

  const resetForm = () => {
    setFormData({ nome: "", email: "", cargo: "", perfil: "investigador", ativo: true });
    setEditingInvestigador(null);
  };

  const filteredInvestigadores = investigadores.filter(inv =>
    inv.nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (inv.cargo && inv.cargo.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">{t("login.accessDenied")}</h2>
          <p className="text-slate-500 mt-2">{t("investigators.noInvestigatorsDescription")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <Link to={`/${slug}/admin`} className="flex items-center gap-3">
                {empresa?.logo_url ? (
                  <img 
                    src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`}
                    alt={empresa?.nome} 
                    className="w-10 h-10 object-contain"
                  />
                ) : (
                  <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="font-semibold text-[#1a1a2e]">{empresa?.nome}</h1>
                  <p className="text-xs text-slate-500">Compliance</p>
                </div>
              </Link>
              <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            <Link to={`/${slug}/admin`} className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
              <LayoutDashboard className="w-5 h-5" />
              {t("dashboard.menu.dashboard")}
            </Link>
            <Link to={`/${slug}/admin/casos`} className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
              <FileText className="w-5 h-5" />
              {t("dashboard.menu.cases")}
            </Link>
            {(user?.role === "admin" || user?.role === "gestor") && (
              <Link to={`/${slug}/admin/investigadores`} className="flex items-center gap-3 px-4 py-3 text-[#1a1a2e] bg-slate-100 rounded-lg font-medium">
                <Users className="w-5 h-5" />
                {t("dashboard.menu.investigators")}
              </Link>
            )}
            {user?.role === "admin" && (
              <Link to={`/${slug}/admin/configuracoes`} className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
                <Settings className="w-5 h-5" />
                {t("dashboard.menu.settings", "Configurações")}
              </Link>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#1a1a2e] rounded-full flex items-center justify-center text-white font-medium">
                {user?.nome?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1a1a2e] truncate">{user?.nome}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full justify-start" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              {t("dashboard.menu.logout")}
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </Button>
          <h1 className="font-semibold text-[#1a1a2e]">{t("investigators.title")}</h1>
          <div className="w-10" />
        </div>

        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a1a2e] hidden lg:block">{t("investigators.title")}</h1>
            <p className="text-slate-500">{t("investigators.subtitle")}</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1a1a2e] hover:bg-[#1a1a2e]/90" data-testid="btn-add-investigador">
                <Plus className="w-4 h-4 mr-2" />
                {t("investigators.addNew")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingInvestigador ? t("investigators.editInvestigator") : t("investigators.addNew")}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="nome">{t("investigators.name")} *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder={t("investigators.form.namePlaceholder")}
                      required
                      data-testid="input-investigador-nome"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">{t("investigators.email")} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t("investigators.form.emailPlaceholder")}
                      required
                      data-testid="input-investigador-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cargo">{t("investigators.role")} ({t("common.optional")})</Label>
                    <Input
                      id="cargo"
                      value={formData.cargo}
                      onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                      placeholder={t("investigators.form.rolePlaceholder")}
                      data-testid="input-investigador-cargo"
                    />
                  </div>
                  <div>
                    <Label htmlFor="perfil">Perfil de acesso *</Label>
                    <select
                      id="perfil"
                      value={formData.perfil}
                      onChange={(e) => setFormData({ ...formData, perfil: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                      data-testid="select-investigador-perfil"
                    >
                      <option value="investigador">Investigador (vê apenas casos atribuídos)</option>
                      <option value="diretor">Diretor (vê todos os casos)</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="ativo">{t("investigators.active")}</Label>
                    <Switch
                      id="ativo"
                      checked={formData.ativo}
                      onCheckedChange={(checked) => setFormData({ ...formData, ativo: checked })}
                      data-testid="switch-investigador-ativo"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      className="bg-[#1a1a2e] hover:bg-[#1a1a2e]/90"
                      disabled={submitting}
                      data-testid="btn-submit-investigador"
                    >
                      {submitting ? t("common.loading") : editingInvestigador ? t("common.save") : t("common.add")}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("investigators.searchPlaceholder")}
              className="pl-10"
              data-testid="search-investigadores"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1a1a2e]/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#1a1a2e]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a1a2e]">{investigadores.length}</p>
                <p className="text-sm text-slate-500">{t("investigators.stats.total")}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {investigadores.filter(i => i.ativo).length}
                </p>
                <p className="text-sm text-slate-500">{t("investigators.stats.active")}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <UserX className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-400">
                  {investigadores.filter(i => !i.ativo).length}
                </p>
                <p className="text-sm text-slate-500">{t("investigators.stats.inactive")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a2e]"></div>
          </div>
        ) : filteredInvestigadores.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {searchQuery ? t("common.noResults") : t("investigators.noInvestigators")}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchQuery
                ? ""
                : t("investigators.noInvestigatorsDescription")}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-[#1a1a2e] hover:bg-[#1a1a2e]/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("investigators.addNew")}
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">{t("investigators.name")}</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">{t("investigators.email")}</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">{t("investigators.role")}</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Perfil</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">{t("investigators.status")}</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">{t("caseDetails.createdAt")}</th>
                    <th className="text-right px-6 py-3 text-sm font-medium text-slate-600">{t("investigators.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredInvestigadores.map((inv) => (
                    <tr key={inv.id} className="hover:bg-slate-50 transition-colors" data-testid={`investigador-row-${inv.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-[#1a1a2e]/10 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-[#1a1a2e]">
                              {inv.nome.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-slate-900">{inv.nome}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {inv.email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {inv.cargo ? (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Briefcase className="w-4 h-4 text-slate-400" />
                            {inv.cargo}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          inv.perfil === "diretor"
                            ? "bg-violet-100 text-violet-700"
                            : "bg-blue-100 text-blue-700"
                        }`}>
                          {inv.perfil === "diretor" ? (
                            <><Eye className="w-3.5 h-3.5" /> Diretor</>
                          ) : (
                            <><Search className="w-3.5 h-3.5" /> Investigador</>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleAtivo(inv)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                            inv.ativo
                              ? "bg-green-100 text-green-700 hover:bg-green-200"
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                          data-testid={`toggle-status-${inv.id}`}
                        >
                          {inv.ativo ? (
                            <>
                              <UserCheck className="w-3.5 h-3.5" />
                              {t("investigators.active")}
                            </>
                          ) : (
                            <>
                              <UserX className="w-3.5 h-3.5" />
                              {t("investigators.inactive")}
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-slate-500 text-sm">
                        {formatDate(inv.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            title="Redefinir senha"
                            onClick={() => handleResetPassword(inv)}
                            data-testid={`btn-reset-password-${inv.id}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(inv)}
                            data-testid={`btn-edit-${inv.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setInvestigadorToDelete(inv);
                              setDeleteConfirmOpen(true);
                            }}
                            data-testid={`btn-delete-${inv.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Generated Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Credenciais de acesso criadas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              A conta de <strong>{createdUserName}</strong> foi criada com sucesso. 
              Compartilhe as credenciais abaixo com o usuário:
            </p>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-3">
              <div>
                <p className="text-xs text-slate-500 mb-1">Login URL</p>
                <p className="text-sm font-mono text-slate-800">{window.location.origin}/{slug}/login</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Senha gerada</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-slate-200 rounded px-3 py-2 text-base font-mono tracking-wider text-slate-900" data-testid="generated-password">
                    {generatedPassword}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPassword);
                      setPasswordCopied(true);
                      setTimeout(() => setPasswordCopied(false), 2000);
                    }}
                    data-testid="btn-copy-password"
                  >
                    {passwordCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                Esta senha é exibida apenas uma vez. Copie e envie ao usuário antes de fechar esta janela.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setPasswordDialogOpen(false)} className="bg-[#1a1a2e] hover:bg-[#1a1a2e]/90">
              Entendi, fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("investigators.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("investigators.deleteWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
